import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchEmissionsByTop10Entities } from '../reportsSlice';
import { LoadingState } from '../../../utilities/constants';
import { useSelector } from 'react-redux';
import { HorizontalBar } from 'react-chartjs-2';
import { Select } from '../configurations/Select';
import ReportContainer from '../ReportContainer';
import { useKeycloak } from '@react-keycloak/web';
import { useHistory } from 'react-router-dom';
import {
  setupReportRangePeriod,
  getCurrentReportPeriod,
  getReportYearTypeOptions,
  getReportRangePeriod,
  parseFavouriteData,
  getEmissionTypeOptions,
  reportYearList,
  generateRandomColors,
  generateChartLegend,
  customFilterPeriodOptions,
  reportUnitByTypeId,
  formatNumber,
  parseUnitJson,
  displayTargetUnit,
  createInitChartConfig,
  createInitChartPlugins,
} from '../reportHelper';
import { useTranslation } from 'react-i18next';

const EmissionsByTop10Entities = () => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const history = useHistory();
  const keycloakObject = useKeycloak();
  const { filterData } = parseFavouriteData(useLocation().search);
  const userInfo = useSelector((state) => state.users.user);
  const currentReportPeriod = getCurrentReportPeriod(userInfo.usrOrgOptions);
  const reportRangePeriod = getReportRangePeriod(userInfo.usrOrgOptions, currentReportPeriod.reportYear);
  const [filterParams, setFilterParams] = useState({
    reportYearType: 'Calendar Year',
    reportYear: new Date().getUTCFullYear(),
    emissionTypeId: 1,
    ...currentReportPeriod,
    ...(filterData || {})
  });
  const [chartData, setChartData] = useState({});
  const emissionsByTop10Entities = useSelector((state) => state.reports.emissionsByTop10Entities);
  const emissionsByTop10EntitiesStatus = useSelector((state) => state.reports.emissionsByTop10EntitiesStatus);

  const selectedReportYearTypeHandler = ({ target }) => {
    if (target.value === 'SETUP') {
      setupReportRangePeriod(keycloakObject, history);
    }
  };

  const plugins = createInitChartPlugins([], {
    displayChartAxesTotal: () => chartData.datasets && chartData.datasets.length > 0 &&
      emissionsByTop10EntitiesStatus === LoadingState.succeeded,
  });

  const add = (accumulator, a) => {
    return accumulator + a;
  };

  const getLabels = useCallback(() => {
    const labels = [];
    const data = [];
    const { unit } = reportUnitByTypeId(parseInt(filterParams.emissionTypeId), t);
    if (emissionsByTop10EntitiesStatus === LoadingState.succeeded) {
      const insert = (arr, index, newItem) => [
        ...arr.slice(0, index),
        newItem,
        ...arr.slice(index, emissionsByTop10Entities.length - 1)
      ];
      generateRandomColors(true);
      emissionsByTop10Entities.map((item, itemIndex) => {
        if (labels.includes(item.entityName) === false) {
          labels.push(item.entityName);
          item.children.map((eps) => {
            const dataList = new Array(emissionsByTop10Entities.length - 1).fill(0);
            const foundIndex = data.findIndex((x) => x.label === eps.eps_name);
            if (foundIndex === -1) {
              data.push({
                axis: 'y',
                label: eps.eps_name,
                data: insert(
                  dataList,
                  itemIndex,
                  eps.co2_et
                    ? !isNaN(eps.co2_et)
                      ? parseFloat(eps.co2_et)
                      : 0
                    : !isNaN(eps.consumption)
                      ? parseFloat(eps.consumption)
                      : 0
                ),
                backgroundColor: generateRandomColors(),
                entityName: item.entityName,
                units: parseUnitJson(eps.unit),
              });
            } else {
              parseUnitJson(eps.unit, data[foundIndex].units);
              data[foundIndex].entityName !== item.entityName
                ? (data[foundIndex].data = insert(
                  data[foundIndex].data,
                  itemIndex,
                  eps.co2_et ? parseFloat(eps.co2_et) : parseFloat(eps.consumption)
                ))
                : (data[foundIndex].data[itemIndex] =
                  data[foundIndex].data[itemIndex] + parseFloat(eps.co2_et ? eps.co2_et : eps.consumption));
            }
            return data;
          });
        }
        return labels;
      });

      data.forEach((singleDataset) => {
        singleDataset.title = singleDataset.label;
        singleDataset.total = singleDataset.data.reduce(add, 0);
        singleDataset.displayUnit = displayTargetUnit(singleDataset.units, unit);
        const { label, total, displayUnit } = singleDataset;
        singleDataset.label = `${label} (${formatNumber(total)} ${displayUnit})`;
      });
    }
    return { labels: labels, datasets: data };
  }, [t, emissionsByTop10EntitiesStatus, emissionsByTop10Entities, filterParams.emissionTypeId]);

  const options = useMemo(() => {
    const totalArray = [];
    if (chartData.datasets && chartData.datasets.length > 0 && emissionsByTop10EntitiesStatus === LoadingState.succeeded) {
      chartData.datasets[0].data.map((dataPoint, index) => {
        let total = dataPoint;
        let i = 1;
        while (i < chartData.datasets.length) {
          total += chartData.datasets[i].data[index];
          i++;
        }
        return totalArray.push(total);
      });
    }
    const { title } = reportUnitByTypeId(parseInt(filterParams.emissionTypeId), t);
    return createInitChartConfig({
      interaction: {
        mode: 'interpolate',
        intersect: false,
        axis: 'x'
      },
      scales: {
        xAxes: [
          {
            stacked: true,
            ticks: {
              beginAtZero: true,
              suggestedMax: Math.max(...totalArray) + (5 / 100) * Math.max(...totalArray)
            },
            scaleLabel: {
              display: true,
              labelString: title,
            }
          }
        ],
        yAxes: [
          {
            stacked: true,
            gridLines: {
              display: false
            }
          }
        ]
      },
      tooltips: {
        filter: function (tooltipItem, data) {
          let value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
          return value !== 0;
        },
        callbacks: {
          label: (tooltipItem, data) => {
            const { title, displayUnit } = data.datasets[tooltipItem.datasetIndex];
            return `${title}: ${formatNumber(tooltipItem.value)} ${displayUnit}`;
          }
        },
      },
    });
  }, [t, chartData.datasets, emissionsByTop10EntitiesStatus, filterParams.emissionTypeId]);

  useEffect(() => {
    if (emissionsByTop10EntitiesStatus === LoadingState.loading) {
      setChartData({});
    }
    if (emissionsByTop10EntitiesStatus === LoadingState.succeeded) {
      setChartData(getLabels());
    }
  }, [
    emissionsByTop10EntitiesStatus, emissionsByTop10Entities, getLabels,
  ]);

  useEffect(() => {
    generateChartLegend(chartRef);
  }, [emissionsByTop10EntitiesStatus, chartData]);

  const fetchEmissionsByTop10EntitiesHandler = async (dispatch, payload) => {
    payload.data = {
      reportYearType: filterParams.reportYearType,
      reportYear: parseInt(filterParams.reportYear),
      emissionTypeId: parseInt(filterParams.emissionTypeId),
      ...payload.data,
    };
    return await dispatch(fetchEmissionsByTop10Entities(payload));
  };

  const getFavouriteFilterDataHandler = (params, filters) => {
    const paramsData = {
      ...params,
      reportYearType: filters.reportYearType,
      reportYear: parseInt(filters.reportYear),
      emissionTypeId: parseInt(filters.emissionTypeId),
    };
    setFilterParams(paramsData);
    return paramsData;
  };

  const parameters = [
    {
      key: t('Report Year'),
      value: filterParams.reportYear,
    },
  ];


  const chartRender = () => {
    return (
      <>
        <div className="chart-graph">
          <HorizontalBar
            data={chartData}
            options={options}
            ref={chartRef}
            plugins={plugins}
          />
        </div>
        <div id="js-legend" className="chart-legend"></div>
      </>
    )
  };

  const extraConfigRender = (isFavReport) => {
    return (
      <>
        <Select
          disabled={isFavReport}
          label={t("Report Year Type")}
          name="reportYearType"
          options={getReportYearTypeOptions(t, reportRangePeriod)}
          onChange={selectedReportYearTypeHandler}
        />
        <Select
          disabled={isFavReport}
          label={t("Report Year")}
          name="reportYear"
          options={reportYearList(currentReportPeriod?.financialYear)}
        />
        <Select
          disabled={isFavReport}
          label={t("CO2-e (t) or Usage")}
          name="emissionTypeId"
          options={getEmissionTypeOptions(t)}
        />
      </>
    );
  };

  return (
    <ReportContainer
      reportName={t('Emissions by Top 10 Entities')}
      reportSubTitle={t('Top Ten Entities by Metric')}
      fetchReportData={fetchEmissionsByTop10EntitiesHandler}
      fetchReportStatus={emissionsByTop10EntitiesStatus}
      reportData={emissionsByTop10Entities}
      reportRender={chartRender}
      extraConfigRender={extraConfigRender}
      getFavouriteFilterData={getFavouriteFilterDataHandler}
      extraConfigDefault={{
        reportYearType: filterParams.reportYearType,
        reportYear: parseInt(filterParams.reportYear),
        emissionTypeId: parseInt(filterParams.emissionTypeId),
      }}
      chartRef={chartRef}
      extraParameters={parameters}
      isUseEndDate={false}
      isUseStartDate={false}
      getTimeSelectOptions={customFilterPeriodOptions}
    />
  );
};

export default EmissionsByTop10Entities;
